import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader } from '../../components/common';
import { FiTrendingUp, FiTrendingDown, FiPlus, FiTrash2 } from 'react-icons/fi';

interface WatchListItem {
  id: number;
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  category: 'stock' | 'crypto' | 'gold';
}

// Mock watchlist data
const mockWatchList: WatchListItem[] = [
  {
    id: 1,
    symbol: 'RELIANCE',
    name: 'Reliance Industries',
    price: 2456.75,
    change: 45.30,
    changePercent: 1.88,
    category: 'stock',
  },
  {
    id: 2,
    symbol: 'BTC',
    name: 'Bitcoin',
    price: 5234567.00,
    change: -89234.00,
    changePercent: -1.68,
    category: 'crypto',
  },
  {
    id: 3,
    symbol: 'TCS',
    name: 'Tata Consultancy Services',
    price: 3892.50,
    change: 23.15,
    changePercent: 0.60,
    category: 'stock',
  },
  {
    id: 4,
    symbol: 'ETH',
    name: 'Ethereum',
    price: 287654.00,
    change: 5432.00,
    changePercent: 1.93,
    category: 'crypto',
  },
  {
    id: 5,
    symbol: 'GOLD',
    name: 'Gold (24K)',
    price: 6234.00,
    change: -12.50,
    changePercent: -0.20,
    category: 'gold',
  },
  {
    id: 6,
    symbol: 'INFY',
    name: 'Infosys Limited',
    price: 1567.80,
    change: -18.45,
    changePercent: -1.16,
    category: 'stock',
  },
  {
    id: 7,
    symbol: 'HDFC',
    name: 'HDFC Bank',
    price: 1623.40,
    change: 32.70,
    changePercent: 2.05,
    category: 'stock',
  },
];

const WatchList: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [loading, setLoading] = useState(false);
  const [watchList, setWatchList] = useState<WatchListItem[]>([]);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    fetchWatchList();
  }, []);

  const fetchWatchList = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setWatchList(mockWatchList);
      setLoading(false);
    }, 500);
  };

  const formatPrice = (price: number) => {
    if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)}L`;
    }
    return `₹${price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleRemoveItem = (id: number) => {
    setWatchList((prev) => prev.filter((item) => item.id !== id));
  };

  const handleItemClick = (item: WatchListItem) => {
    if (editMode) return;
    // Navigate to details based on category
    switch (item.category) {
      case 'stock':
        navigate(`/market/stocks/${item.id}`);
        break;
      case 'crypto':
        navigate(`/market/crypto/${item.id}`);
        break;
      case 'gold':
        navigate('/market/gold/details');
        break;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'stock':
        return colors.primary;
      case 'crypto':
        return '#F7931A'; // Bitcoin orange
      case 'gold':
        return '#FFD700'; // Gold
      default:
        return colors.primary;
    }
  };

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {loading && <CLoader />}

      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)}>
            <CText type="B16" color={colors.textColor}>←</CText>
          </button>
          <CText type="B18" color={colors.textColor}>
            Watchlist
          </CText>
          <button onClick={() => setEditMode(!editMode)}>
            <CText type="M14" color={colors.primary}>
              {editMode ? 'Done' : 'Edit'}
            </CText>
          </button>
        </div>

        {/* Summary */}
        <div
          className="rounded-xl p-4 mb-4"
          style={{ backgroundColor: colors.inputBackground }}
        >
          <div className="flex justify-between items-center">
            <div>
              <CText type="r12" color={colors.grayScale500}>
                Total Items
              </CText>
              <CText type="B24" color={colors.textColor}>
                {watchList.length}
              </CText>
            </div>
            <button
              onClick={() => navigate('/market')}
              className="flex items-center gap-2 px-4 py-2 rounded-full"
              style={{ backgroundColor: colors.primary }}
            >
              {React.createElement(FiPlus as any, { size: 16, color: colors.white })}
              <CText type="M12" color={colors.white}>
                Add
              </CText>
            </button>
          </div>
        </div>
      </div>

      {/* Watchlist Items */}
      <div className="px-4">
        {watchList.length === 0 ? (
          <div className="text-center py-12">
            <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 16 }}>
              Your watchlist is empty
            </CText>
            <button
              onClick={() => navigate('/market')}
              className="px-6 py-3 rounded-full"
              style={{ backgroundColor: colors.primary }}
            >
              <CText type="M14" color={colors.white}>
                Browse Market
              </CText>
            </button>
          </div>
        ) : (
          watchList.map((item) => (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              className="w-full flex items-center justify-between py-4 border-b"
              style={{ borderColor: colors.borderColor }}
            >
              {/* Left: Symbol & Name */}
              <div className="flex items-center gap-3">
                {editMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveItem(item.id);
                    }}
                    className="p-2 rounded-full"
                    style={{ backgroundColor: colors.alertColor + '20' }}
                  >
                    {React.createElement(FiTrash2 as any, { size: 16, color: colors.alertColor })}
                  </button>
                )}
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{
                    backgroundColor: getCategoryColor(item.category) + '20',
                    color: getCategoryColor(item.category),
                  }}
                >
                  {item.symbol.substring(0, 3)}
                </div>
                <div className="text-left">
                  <CText type="M14" color={colors.textColor}>
                    {item.symbol}
                  </CText>
                  <CText type="r12" color={colors.grayScale500}>
                    {item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name}
                  </CText>
                </div>
              </div>

              {/* Right: Price & Change */}
              <div className="text-right">
                <CText type="M14" color={colors.textColor}>
                  {formatPrice(item.price)}
                </CText>
                <div className="flex items-center justify-end gap-1">
                  {React.createElement(
                    item.change >= 0 ? FiTrendingUp : FiTrendingDown as any,
                    { size: 12, color: item.change >= 0 ? colors.green : colors.alertColor }
                  )}
                  <CText
                    type="r12"
                    color={item.change >= 0 ? colors.green : colors.alertColor}
                  >
                    {item.change >= 0 ? '+' : ''}{item.changePercent.toFixed(2)}%
                  </CText>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default WatchList;
